import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/**
 * Generate a PDF report of menu items grouped by category.
 * Columns: Name, Size, Food Cost, Avg Price, FC%.
 * Opens in a new browser tab.
 *
 * @param {Object} params
 * @param {Array} params.data - Menu items array from getMenuItems (includes food_cost, avg_price)
 */
export function generateMenuItemsPdfReport({ data }) {
  if (!data?.length) return { error: "No menu items to export." };

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;

  // ── Header ──────────────────────────────────────────────────────────────
  let y = margin;
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("Pizza Felice LLC", margin, y);
  doc.text("Menu Items", pageWidth / 2, y, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.text("Internal", pageWidth - margin, y, { align: "right" });
  y += 6;

  // ── Subtitle: item count ───────────────────────────────────────────────
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(`${data.length} items`, margin, y);
  doc.setTextColor(0, 0, 0);
  y += 6;

  // ── Group items by category ────────────────────────────────────────────
  const grouped = {};
  data.forEach((item) => {
    const cat = item.category_name || "Uncategorized";
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(item);
  });

  // ── Build table body with category group headers ───────────────────────
  const body = [];
  Object.keys(grouped).forEach((cat) => {
    // Category header row (spans all columns)
    body.push([
      { content: cat, colSpan: 5, styles: { fontStyle: "bold", fillColor: [240, 240, 240], textColor: [50, 50, 50] } },
    ]);
    // Item rows
    grouped[cat].forEach((item) => {
      const foodCost = Number(item.food_cost || 0);
      const avgPrice = item.avg_price ? Number(item.avg_price) : null;
      // FC% = food cost / avg selling price × 100
      const fcPct = avgPrice && avgPrice > 0 ? Math.round((foodCost / avgPrice) * 1000) / 10 : null;
      body.push([
        item.name || "",
        item.size || "",
        fmtAmt(foodCost),
        avgPrice ? fmtAmt(avgPrice) : "-",
        fcPct != null ? `${fcPct}%` : "-",
      ]);
    });
  });

  // ── Render table ───────────────────────────────────────────────────────
  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [["Menu Item", "Size", "Food Cost", "Avg Price", "FC %"]],
    body,
    theme: "grid",
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: {
      fillColor: [97, 97, 97],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 8,
    },
    columnStyles: {
      0: { cellWidth: "auto" },
      1: { cellWidth: 22, halign: "center" },
      2: { cellWidth: 28, halign: "right" },
      3: { cellWidth: 28, halign: "right" },
      4: { cellWidth: 22, halign: "right" },
    },
  });

  // ── Footer on all pages — timestamp + page numbers ─────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  const now = new Date();
  const dateStr = now.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  const timeStr = now.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(130, 130, 130);
    doc.setFont("helvetica", "normal");
    doc.text(`Generated on ${dateStr} at ${timeStr}`, margin, pageHeight - 6);
    doc.text(`Page ${i} / ${totalPages}`, pageWidth - margin, pageHeight - 6, { align: "right" });
  }

  // Open in new tab
  doc.setTextColor(0, 0, 0);
  const blob = doc.output("blob");
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank");

  return { success: true };
}

/** Format currency amount — always 2 decimal places */
function fmtAmt(val) {
  const n = Number(val || 0);
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
